/**
 * UTM persistence across the signup funnel.
 *
 * Why this exists: Step 1 of registration (POST /auth/register) is the
 * only point where we can attach attribution data to the user record
 * before the Brevo lead is created. Steps 2/3 are reached via React
 * Router navigation, which preserves window history but does not
 * carry along query-string parameters from the original landing URL.
 *
 * Behaviour:
 *  - On every page load we capture any incoming utm_* params from
 *    `window.location.search` and merge them into sessionStorage.
 *  - sessionStorage survives Steps 1→2→3 inside the same tab but is
 *    discarded when the user closes the browser. This matches what a
 *    typical marketing-attribution model expects (a "session" UTM).
 *  - First-wins: if `notalia_utms` already has values, we don't
 *    overwrite them with empty values from a subsequent navigation.
 *    Explicit new utm_* params do replace prior ones — they probably
 *    reflect a fresh campaign click in the same session.
 *  - `getStoredUtms()` returns the persisted dict (empty when nothing
 *    was captured). Callers spread it into their request payload.
 *
 * Hard rule: this module never throws. If sessionStorage is unavailable
 * (privacy mode, server-side render, etc), it gracefully returns {}.
 */

const STORAGE_KEY = "notalia_utms";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

function _safeStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    // Touch the storage so Safari private mode throws here rather than later.
    window.sessionStorage.getItem(STORAGE_KEY);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function _parseFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const out = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 255);
    });
    return out;
  } catch {
    return {};
  }
}

/**
 * Reads any utm_* params from the current URL and merges them into
 * sessionStorage. Safe to call repeatedly. No-ops when no UTM params
 * are present and storage already has prior values.
 */
export function captureUtmsFromUrl() {
  const storage = _safeStorage();
  if (!storage) return {};
  const incoming = _parseFromUrl();
  if (Object.keys(incoming).length === 0) {
    // Nothing new in the URL — keep whatever we already stored.
    return getStoredUtms();
  }
  // Merge: incoming overrides storage on a per-key basis. This means a
  // fresh ad-click within the same session correctly reattributes.
  const existing = getStoredUtms();
  const merged = { ...existing, ...incoming };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* quota or disabled — ignore */
  }
  return merged;
}

/** Returns the persisted UTM dict, or {} if nothing was captured. */
export function getStoredUtms() {
  const storage = _safeStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Filter to only the recognised keys — defends against tampering.
    const out = {};
    UTM_KEYS.forEach((k) => {
      if (typeof parsed[k] === "string" && parsed[k]) out[k] = parsed[k];
    });
    return out;
  } catch {
    return {};
  }
}

/** Removes stored UTMs. Use after the signup completes (optional). */
export function clearStoredUtms() {
  const storage = _safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
